import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { loadFeatureFlags } from "./featureFlagsActions";
import { FEATURE_SWITCH_CHANGED_EVENT } from "../../General/featureSwitchApi";

/**
 * Read a feature flag from the centralized store.
 *
 * Optimistic default: returns `true` when the store hasn't loaded yet
 * (matches HeaderMenu's existing fail-safe), or when the task isn't in
 * the store. Override via the second argument:
 *   const enabled = useFeatureFlag(7);            // default true
 *   const enabled = useFeatureFlag(7, false);     // default false
 */
export function useFeatureFlag(taskId, defaultValue = true) {
  const value = useSelector(
    (state) => state.featureFlags?.byTaskId?.[Number(taskId)]
  );
  return value === undefined ? defaultValue : value;
}

/** Read the current user's own stub-mode state from the store. */
export function useStubModeEnabled() {
  return useSelector((state) => !!state.featureFlags?.stubEnabled);
}

/** True once the initial load has resolved (success or failure). */
export function useFeatureFlagsLoaded() {
  return useSelector((state) => !!state.featureFlags?.loaded);
}

/**
 * Mount-once loader. Triggers a refetch on auth change and on the
 * `feature-switch-changed` event so admin toggles propagate without a
 * page reload.
 */
export function useFeatureFlagsLoader() {
  const dispatch = useDispatch();
  const user = useSelector((s) => s.auth?.user);

  useEffect(() => {
    if (!user?.userID) return;
    dispatch(loadFeatureFlags(user));
    const onChange = () => dispatch(loadFeatureFlags(user));
    window.addEventListener(FEATURE_SWITCH_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(FEATURE_SWITCH_CHANGED_EVENT, onChange);
  }, [user, dispatch]);
}
